import {
  DateInput,
  type ModalProps,
  PropertyList,
  ScholarshipSubStatusBadge,
  SelectInput,
  UnsavedChangesModal
} from '@app/components'
import { rosterTuitionCredit, showMessagesNotifications, toScholarshipStatus, useUpdateEnrollment } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarEnrollmentItem } from '@app/types'
import { Button, Group, Text } from '@mantine/core'
import { useDidUpdate, useDisclosure } from '@mantine/hooks'
import { IconCheck, IconEdit, IconX } from '@tabler/icons-react'
import { useForm, yupResolver } from '@mantine/form'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import minMax from 'dayjs/plugin/minMax'
import { type FC, useCallback, useMemo, useState } from 'react'
import * as yup from 'yup'

dayjs.extend(isSameOrAfter)
dayjs.extend(localizedFormat)
dayjs.extend(minMax)

type FormData = {
  endDate: Date | null
  participationType: string
  participationTypeChangeDate: Date | null
  startDate: Date | null
}

export interface EnrollmentViewModalProps extends Omit<ModalProps, 'children' | 'opened' | 'title'> {
  enrollmentItem: SugarEnrollmentItem | null
}

export const EnrollmentViewModal: FC<EnrollmentViewModalProps> = ({ enrollmentItem, onClose, ...props }) => {
  const { getOptionLabelByValue, options, siteId } = useSugarContext()
  const { isUpdatingEnrollment, updateEnrollment } = useUpdateEnrollment()
  const [changed, setChanged] = useState(false)
  const [editing, editHandler] = useDisclosure(false)
  const [data, setData] = useState<SugarEnrollmentItem | null>(enrollmentItem)
  const initialValues = useMemo<FormData>(
    () => ({
      endDate: data?.enrollment.endDate ? dayjs(data.enrollment.endDate).toDate() : null,
      participationType: data?.enrollment.participationType ?? '',
      participationTypeChangeDate: null,
      startDate: data?.enrollment.startDate ? dayjs(data.enrollment.startDate).toDate() : null
    }),
    [data]
  )
  const form = useForm<FormData>({
    initialValues,
    validate: yupResolver(
      yup.object().shape(
        {
          endDate: yup
            .date()
            .nullable()
            .when('startDate', ([startDate], schema) =>
              startDate ? schema.min(dayjs(startDate).add(1, 'day').toDate(), 'Must be after start date!') : schema
            )
            .test('approvalDate', 'Must be on or after approval date!', (value) =>
              value && data?.application.approvalDate
                ? dayjs(value).isSameOrAfter(data.application.approvalDate, 'day')
                : true
            ),
          participationType: yup.string().required('Participation type is required!'),
          participationTypeChangeDate: yup
            .date()
            .required('Effective date is required!')
            .test('approvalDate', 'Must be on or after approval date!', (value) =>
              data?.application.approvalDate ? dayjs(value).isSameOrAfter(data.application.approvalDate, 'day') : true
            ),
          startDate: yup
            .date()
            .required('Start date is required!')
            .when('endDate', ([endDate], schema) =>
              endDate ? schema.max(dayjs(endDate).subtract(1, 'day').toDate(), 'Must be before end date!') : schema
            )
            .test('approvalDate', 'Must be on or after approval date!', (value) =>
              data?.application.approvalDate ? dayjs(value).isSameOrAfter(data.application.approvalDate, 'day') : true
            )
        },
        [['startDate', 'endDate']]
      )
    ),
    validateInputOnChange: true
  })

  const minStartDate = useMemo(
    () => (data?.application.approvalDate ? dayjs(data.application.approvalDate).toDate() : undefined),
    [data?.application.approvalDate]
  )
  const minEndDate = useMemo(() => {
    const startPlusOne = form.values.startDate ? dayjs(form.values.startDate).add(1, 'day') : undefined
    const approval = data?.application.approvalDate ? dayjs(data.application.approvalDate) : undefined
    if (startPlusOne && approval) return dayjs.max(startPlusOne, approval).toDate()
    return (startPlusOne ?? approval)?.toDate()
  }, [form.values.startDate, data?.application.approvalDate])
  const minEffectiveDate = useMemo(
    () => (data?.application.approvalDate ? dayjs(data.application.approvalDate).toDate() : undefined),
    [data?.application.approvalDate]
  )

  const resetForm = useCallback(
    (values: FormData) => {
      form.setValues(values)
      form.resetDirty(values)
    },
    [form]
  )
  const handleCancel = useCallback(() => {
    resetForm(initialValues)
    editHandler.close()
  }, [editHandler, initialValues, resetForm])
  const handleClose = useCallback(() => {
    handleCancel()
    onClose()
  }, [handleCancel, onClose])
  const handleSubmit = useCallback(async () => {
    if (siteId && data?.enrollment.id && data?.enrollment.programYearId) {
      const updated = await updateEnrollment({
        endDate: form.values.endDate ? dayjs(form.values.endDate).format('YYYY-MM-DD') : null,
        enrollmentId: data.enrollment.id,
        participationType: form.values.participationType,
        participationTypeChangeDate: form.values.participationTypeChangeDate
          ? dayjs(form.values.participationTypeChangeDate).format('YYYY-MM-DD')
          : null,
        programYearId: data.enrollment.programYearId,
        siteId,
        startDate: dayjs(form.values.startDate).format('YYYY-MM-DD')
      })

      if (updated.data) {
        setData(updated.data)
      }

      showMessagesNotifications(updated.messages)

      handleClose()
    }
  }, [
    siteId,
    data?.enrollment.id,
    data?.enrollment.programYearId,
    updateEnrollment,
    form.values.endDate,
    form.values.participationType,
    form.values.participationTypeChangeDate,
    form.values.startDate,
    handleClose
  ])

  const isDirty = form.isDirty()
  const isValid = form.isValid()

  useDidUpdate(() => {
    if (enrollmentItem) {
      setData(enrollmentItem)
    }
  }, [enrollmentItem])

  useDidUpdate(() => {
    resetForm(initialValues)
  }, [initialValues])

  useDidUpdate(() => {
    setChanged(isDirty)
  }, [isDirty])

  const tuitionCredit = data ? rosterTuitionCredit(data) : null

  return (
    <UnsavedChangesModal
      closeOnClickOutside
      closeOnEscape
      isChanged={changed}
      isLoading={isUpdatingEnrollment}
      onClose={handleClose}
      opened={!!enrollmentItem}
      size='lg'
      title={`Student: ${data?.contact.name}`}
      {...props}
    >
      <PropertyList
        items={[
          {
            label: 'Application Status',
            value: data?.application.status ? getOptionLabelByValue('applicationStatus', data.application.status) : '-'
          },
          data?.application.statusDetails
            ? {
                label: 'Application Details',
                value: getOptionLabelByValue('applicationStatusDetails', data.application.statusDetails)
              }
            : null,
          data?.application.approvalDate
            ? {
                label: 'Application Approval',
                value: dayjs(data.application.approvalDate).format('LL')
              }
            : null,
          !editing
            ? {
                label: 'Participation Type',
                value: data?.enrollment.participationType
                  ? getOptionLabelByValue('participationTypes', data.enrollment.participationType)
                  : '-'
              }
            : null,
          {
            label: 'Enrollment Status',
            value: data?.enrollment.status ? getOptionLabelByValue('enrollmentStatus', data.enrollment.status) : '-'
          },
          // MIQ-2883: the roster's rule, so pending and denied applications show
          // no credit here either.
          tuitionCredit
            ? {
                label: 'Enrollment Tuition Credit',
                value: `${tuitionCredit}/mo`
              }
            : null,
          {
            label: 'DPP Scholarship',
            value: (
              <Group spacing='xs'>
                {/* Matches the roster column: the API's tri-state, not the view
                    labels. These two must move together. Guarded on the enrollment
                    rather than the status, because toScholarshipStatus collapses a
                    missing value to 'No' — correct once loaded, but it would render
                    a confident 'No' for every student while the modal is still
                    fetching. */}
                <Text size='sm'>
                  {data?.enrollment ? toScholarshipStatus(data.enrollment.gapScholarshipStatus) : '-'}
                </Text>
                {data?.enrollment ? <ScholarshipSubStatusBadge enrollment={data.enrollment} /> : null}
              </Group>
            )
          },
          !editing
            ? {
                label: 'Enrollment Start',
                value: data?.enrollment.startDate ? dayjs(data.enrollment.startDate).format('LL') : '-'
              }
            : null,
          !editing && data?.enrollment.endDate
            ? {
                label: 'Enrollment End',
                value: dayjs(data.enrollment.endDate).format('LL')
              }
            : null
        ]}
      />
      {editing && (
        <>
          <Group
            align='start'
            grow
            spacing='xs'
          >
            <DateInput
              disabled={isUpdatingEnrollment}
              label='Start Date'
              minDate={minStartDate}
              maxDate={form.values.endDate ? dayjs(form.values.endDate).subtract(1, 'day').toDate() : undefined}
              required
              {...form.getInputProps('startDate')}
            />
            <DateInput
              clearable
              disabled={isUpdatingEnrollment}
              label='End Date'
              minDate={minEndDate}
              {...form.getInputProps('endDate')}
            />
          </Group>
          <Group
            align='start'
            grow
            spacing='xs'
          >
            <SelectInput
              data={options?.participationTypes ?? []}
              disabled={isUpdatingEnrollment}
              label='Participation Type'
              required
              tabIndex={4}
              {...form.getInputProps('participationType')}
            />
            <DateInput
              clearable
              disabled={isUpdatingEnrollment}
              label='Effective Date'
              minDate={minEffectiveDate}
              required
              tabIndex={5}
              {...form.getInputProps('participationTypeChangeDate')}
            />
          </Group>
        </>
      )}
      <Group
        mt='xl'
        position='right'
      >
        {editing && (
          <Button
            color='red'
            disabled={isUpdatingEnrollment}
            leftIcon={<IconX size={18} />}
            onClick={handleCancel}
            tabIndex={1}
            variant='subtle'
          >
            Cancel
          </Button>
        )}
        {editing && (
          <Button
            disabled={isUpdatingEnrollment || !isValid}
            leftIcon={<IconCheck size={18} />}
            onClick={handleSubmit}
            tabIndex={6}
          >
            Save
          </Button>
        )}
        {!editing && data?.enrollment.editable && (
          <Button
            leftIcon={<IconEdit size={18} />}
            onClick={editHandler.open}
          >
            Edit
          </Button>
        )}
      </Group>
    </UnsavedChangesModal>
  )
}
